import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';

// ── Phone Numbers (/voice-phone-numbers) ──────────────────────────────

export type VoicePhoneNumber = {
  id: string;
  phone: string;
  friendlyName?: string;
  location?: string;
  status: string;
  owned: boolean;
  isDefault: boolean;
  assignedTo?: { campaign_id: string; campaign_name: string; has_agent?: boolean } | null;
};

export type VoiceSmsRecord = {
  id: string;
  from: string;
  to: string;
  message: string;
  time: string;
};

let phoneNumbersStore: VoicePhoneNumber[] = [];
let smsHistoryStore: VoiceSmsRecord[] = [];

function mapPhoneNumberRow(n: any): VoicePhoneNumber {
  const phone = n.phone_number ?? n.phone ?? n.friendly_name ?? '';
  return {
    id: String(n.sid ?? n.id ?? phone),
    phone: String(phone),
    friendlyName: n.friendly_name,
    location: n.locality ?? n.region,
    status: String(n.status ?? 'active'),
    owned: n.owned !== false,
    isDefault: Boolean(n.is_default),
    assignedTo: n.assigned_to ?? null,
  };
}

export async function fetchVoicePhoneNumbers(axiosInstance?: AxiosInstance): Promise<VoicePhoneNumber[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = apiConfig.voiceEndpoints.phoneNumbers;
  const response = await client.get<any>(url);
  let resData = response.data;
  if (typeof resData === 'string') { try { resData = JSON.parse(resData); } catch(e) {} }
  if (resData && !Array.isArray(resData) && resData.data && typeof resData.data === 'object') {
    resData = resData.data;
  }
  const data = Array.isArray(resData) ? resData : (resData?.numbers || []);
  // Filter out numbers that are explicitly owned: false
  const ownedNumbers = data.filter((n: any) => n.owned !== false);
  return ownedNumbers.map(mapPhoneNumberRow);
}

// ── Phone forwarding verification (/api/phone-numbers/{sid}/verify-forwarding) —
// RESOLVED (2026-08-21) and confirmed live. `{sid}` is the
// Twilio phone number SID already assigned to the campaign (VoicePhoneNumber.id
// above), not the campaign id. Both endpoints are unauthenticated on the backend,
// but calling them through the normal voiceAxios instance is harmless — the
// backend simply doesn't require the bearer token on this route cluster.

export type StartForwardingVerification = {
  verification_id: string;
  call_sid: string | null;
  status: 'pending' | 'in_progress' | 'failed';
};

/** Terminal once `answered | no_answer | busy | failed`, or earlier if `timed_out`
 * flips true (backend computes this after 120s stuck at pending/in_progress with
 * no webhook callback yet) — treat `answered` as pass, everything else as fail. */
export type ForwardingVerification = {
  verification_id: string;
  call_sid: string | null;
  status: 'pending' | 'in_progress' | 'answered' | 'no_answer' | 'busy' | 'failed';
  timed_out: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export async function startForwardingVerification(
  phoneSid: string,
  businessLine: string,
  axiosInstance?: AxiosInstance,
): Promise<StartForwardingVerification> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<StartForwardingVerification>(
    `${apiConfig.voiceEndpoints.phoneNumbers}/${phoneSid}/verify-forwarding`,
    { business_line: businessLine },
  );
  return response.data;
}

export async function fetchForwardingVerification(
  phoneSid: string,
  verificationId: string,
  axiosInstance?: AxiosInstance,
): Promise<ForwardingVerification> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<ForwardingVerification>(
    `${apiConfig.voiceEndpoints.phoneNumbers}/${phoneSid}/verify-forwarding/${verificationId}`,
  );
  return response.data;
}

export async function searchAvailableNumbers(query: { country: string; areaCode?: string; contains?: string }, axiosInstance?: AxiosInstance): Promise<VoicePhoneNumber[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.phoneNumbers}/available`;
  
  const params: Record<string, any> = {
    country: query.country || 'US',
    sms_enabled: true,
    voice_enabled: true,
    limit: 10,
  };
  if (query.areaCode) params.area_code = query.areaCode;
  if (query.contains) params.contains = query.contains;

  const response = await client.get<any>(url, { params });
  let resData = response.data;
  if (typeof resData === 'string') { try { resData = JSON.parse(resData); } catch(e) {} }
  if (resData && !Array.isArray(resData) && resData.data && typeof resData.data === 'object') {
    resData = resData.data;
  }
  const data = Array.isArray(resData) ? resData : (resData?.numbers || []);
  return data.map(mapPhoneNumberRow);
}

export async function buyPhoneNumber(phone: string, friendlyName?: string, axiosInstance?: AxiosInstance): Promise<VoicePhoneNumber> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.phoneNumbers}/buy`;
  const response = await client.post<any>(url, {
    phone_number: phone,
    friendly_name: friendlyName || phone,
  });
  return mapPhoneNumberRow(response.data);
}

export async function assignPhoneNumber(sid: string, campaignId: string, axiosInstance?: AxiosInstance): Promise<void> {
  const client = axiosInstance || getVoiceFallbackClient();
  await client.post(`${apiConfig.voiceEndpoints.phoneNumbers}/assign`, {
    phone_number: sid,
    campaign_id: campaignId,
  });
}

export async function unassignPhoneNumber(sid: string, campaignId: string, axiosInstance?: AxiosInstance): Promise<void> {
  const client = axiosInstance || getVoiceFallbackClient();
  await client.post(`${apiConfig.voiceEndpoints.phoneNumbers}/unassign`, {
    phone_number: sid,
    campaign_id: campaignId,
  });
}

export async function releasePhoneNumber(sid: string, axiosInstance?: AxiosInstance): Promise<void> {
  const client = axiosInstance || getVoiceFallbackClient();
  await client.delete(`${apiConfig.voiceEndpoints.phoneNumbers}/${sid}`);
}

export async function sendSms(input: { from: string; to: string; message: string }, axiosInstance?: AxiosInstance): Promise<VoiceSmsRecord> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.phoneNumbers}/send-sms`;
  const response = await client.post<any>(url, {
    to: input.to,
    body: input.message,
    from_number: input.from || null,
  });
  const d = response.data;
  return {
    id: String(d?.id ?? d?.sid ?? `${Date.now()}`),
    from: String(d?.from ?? input.from),
    to: String(d?.to ?? input.to),
    message: String(d?.body ?? input.message),
    time: new Date().toISOString(),
  };
}

export async function fetchRecentSms(axiosInstance?: AxiosInstance): Promise<VoiceSmsRecord[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.voice}/sms/history`;
  const response = await client.get<VoiceSmsRecord[]>(url);
  return response.data;
}

// ── Phone Number extras ───────────────────────────────────────────────

export type TwilioAccountInfo = { account_sid: string; friendly_name: string; balance: string; currency: string };

/** `GET /api/phone-numbers/account` — Twilio account info and balance. */
export async function fetchTwilioAccountInfo(axiosInstance?: AxiosInstance): Promise<TwilioAccountInfo | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`${apiConfig.voiceEndpoints.phoneNumbers}/account`);
  const d = response.data;
  if (!d) return null;
  // balance may come back as a nested object { balance: "92.57", currency: "USD" }
  const balanceRaw = d.balance;
  const balance = typeof balanceRaw === 'object' && balanceRaw !== null
    ? String(balanceRaw.balance ?? balanceRaw.amount ?? '0')
    : String(balanceRaw ?? '0');
  const currency = typeof balanceRaw === 'object' && balanceRaw !== null
    ? String(balanceRaw.currency ?? d.currency ?? 'USD')
    : String(d.currency ?? 'USD');
  return {
    account_sid: String(d.account_sid ?? d.sid ?? ''),
    friendly_name: String(d.friendly_name ?? d.name ?? ''),
    balance,
    currency,
  };
}

/** `GET /api/phone-numbers/sms-logs` — recent SMS message logs. */
export async function fetchSmsLogs(axiosInstance?: AxiosInstance): Promise<VoiceSmsRecord[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`${apiConfig.voiceEndpoints.phoneNumbers}/sms-logs`);
  const list = Array.isArray(response.data) ? response.data : response.data?.messages ?? [];
  return list.map((m: any) => ({
    id: String(m.sid ?? m.id ?? `${Date.now()}`),
    from: String(m.from ?? m.from_number ?? ''),
    to: String(m.to ?? ''),
    message: String(m.body ?? m.message ?? ''),
    time: String(m.date_sent ?? m.created_at ?? ''),
  }));
}
